// client/src/lib/reminder-time.ts
//
// Single authority for all reminder time logic.
//
// Every component that needs to assess, filter, format, or sort reminders by
// time must import from here — never define its own inline version.
//
// Public API
// ──────────
//   timeToMinutes(time)              "08:30"  →  510
//   formatReminderTime(time)         "08:30"  →  "8:30 AM"
//   isReminderPast(time, ref?)       true if time has already passed today
//   filterUpcomingToday(list, ref?)  keep only reminders still ahead today
//   sortByTime(list)                 sort ascending by reminderTime
import { format, parseISO } from 'date-fns';

// ─── Minimal Reminder shape ───────────────────────────────────────────────────

export interface ReminderTimeShape {
  reminderTime?: string | null;
  endTime?: string | null;
  eventEndDate?: string | null;
  startDate?: string | null;
  repeatType?: string | null;
  daysOfWeek?: string[] | null;
}

// ─── Parsing ──────────────────────────────────────────────────────────────────

/**
 * Convert an "HH:MM" string to total minutes since midnight.
 * Returns -1 for null / empty / unparseable input so callers can guard cheaply.
 */
export function timeToMinutes(time: string | null | undefined): number {
  if (!time) return -1;
  const [hStr, mStr] = time.split(':');
  const h = parseInt(hStr ?? '0', 10);
  const m = parseInt(mStr ?? '0', 10);
  if (isNaN(h) || isNaN(m)) return -1;
  return h * 60 + m;
}

// ─── Formatting ───────────────────────────────────────────────────────────────

/**
 * Format an "HH:MM" 24-hour string to "H:MM AM/PM" for display.
 * Returns empty string for null / empty input.
 */
export function formatReminderTime(time: string | null | undefined): string {
  if (!time) return '';
  const [hStr, mStr] = time.split(':');
  const h = parseInt(hStr ?? '0', 10);
  const m = mStr ?? '00';
  if (isNaN(h)) return time;
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${h12}:${m} ${ampm}`;
}

/**
 * Format a reminder's optional end time without inventing an end for
 * point-in-time reminders. The calendar supplies date context separately.
 */
export function formatReminderTimeRange(reminder: ReminderTimeShape): string {
  const start = formatReminderTime(reminder.reminderTime);
  if (!reminder.endTime) return start;
  return `${start}–${formatReminderTime(reminder.endTime)}`;
}

/**
 * Format an explicit multi-date event range when the date context is useful.
 */
export function formatReminderEventRange(reminder: ReminderTimeShape): string {
  const range = formatReminderTimeRange(reminder);
  if (!reminder.endTime || !reminder.eventEndDate || !reminder.startDate || reminder.eventEndDate === reminder.startDate) {
    return range;
  }
  try {
    return `${format(parseISO(`${reminder.startDate}T${reminder.reminderTime || '00:00'}`), 'MMM d, yyyy h:mm a')}–${format(parseISO(`${reminder.eventEndDate}T${reminder.endTime}`), 'MMM d, yyyy h:mm a')}`;
  } catch {
    return `${reminder.startDate} ${formatReminderTime(reminder.reminderTime)}–${reminder.eventEndDate} ${formatReminderTime(reminder.endTime)}`;
  }
}

// ─── Time-state predicates ────────────────────────────────────────────────────

/**
 * Returns true if the reminder's scheduled time has already passed today.
 * "Passed" means strictly before the current minute (< not <=).
 * A reminder at the current minute is still considered active (not past).
 *
 * Pass a `reference` date to override "now" — useful for unit tests and
 * components that hold their own clock state (e.g. OrientationPanel).
 */
export function isReminderPast(
  reminderTime: string | null | undefined,
  reference: Date = new Date()
): boolean {
  const mins = timeToMinutes(reminderTime);
  if (mins < 0) return false;
  const currentMins = reference.getHours() * 60 + reference.getMinutes();
  return mins < currentMins;
}

// ─── Collection helpers ───────────────────────────────────────────────────────

/**
 * Filter a list of reminders to those whose time has NOT yet passed today.
 * Reminders with no time set (always-visible) are always kept.
 *
 * This is the canonical implementation of "today's upcoming reminders" — use
 * this everywhere instead of re-implementing the comparison inline.
 */
export function filterUpcomingToday<T extends ReminderTimeShape>(
  reminders: T[],
  reference: Date = new Date()
): T[] {
  const currentMins = reference.getHours() * 60 + reference.getMinutes();
  return reminders.filter((r) => {
    const mins = timeToMinutes(r.reminderTime);
    if (mins < 0) return true;
    return mins >= currentMins;
  });
}

/**
 * Sort reminders by time ascending (earliest first).
 * Reminders with no time are placed at the end.
 */
export function sortByTime<T extends ReminderTimeShape>(reminders: T[]): T[] {
  return [...reminders].sort((a, b) => {
    const am = timeToMinutes(a.reminderTime);
    const bm = timeToMinutes(b.reminderTime);
    if (am < 0 && bm < 0) return 0;
    if (am < 0) return 1;
    if (bm < 0) return -1;
    return am - bm;
  });
}
